import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  Users, AlertTriangle, Target, ShieldCheck, Search, X,
  BookOpen, DollarSign, Briefcase, GraduationCap, Clock,
  CheckCircle, XCircle, ChevronRight, MessageSquare, Send,
  ArrowLeft, Award, TrendingUp, Calendar, Lightbulb,
  Building, BarChart3, Download, Zap, FileText, ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';
import { NudgeComposer } from '../components/counselor/NudgeComposer';
import { RequirementsMatrix } from '../components/counselor/RequirementsMatrix';
import { MeetingsPanel } from '../components/counselor/MeetingsPanel';
import { TimeSavedPanel } from '../components/counselor/TimeSavedPanel';
import CDPEvolutionAlerts from '../components/counselor/CDPEvolutionAlerts';
import CDPCaseloadCompliance from '../components/counselor/CDPCaseloadCompliance';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('futurepath_token')}` } });
const money = (n) => n == null ? '-' : `$${Math.round(n).toLocaleString()}`;

const timeAgo = (iso) => {
  if (!iso) return 'Never';
  const days = Math.floor((Date.now() - new Date(iso)) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

const TIER = {
  needs_attention: { label: 'Needs Attention', color: '#f87171', bg: 'rgba(248,113,113,0.09)', border: 'rgba(248,113,113,0.28)', icon: AlertTriangle },
  no_direction:    { label: 'No Direction',    color: '#fb923c', bg: 'rgba(251,146,60,0.09)',  border: 'rgba(251,146,60,0.28)',  icon: Target },
  incomplete:      { label: 'Incomplete',      color: '#fbbf24', bg: 'rgba(251,191,36,0.09)',  border: 'rgba(251,191,36,0.28)',  icon: Clock },
  on_track:        { label: 'On Track',        color: '#34d399', bg: 'rgba(52,211,153,0.09)',  border: 'rgba(52,211,153,0.28)',  icon: ShieldCheck },
};

const MODULE_META = {
  discover_me:        { label: 'Strengths & Interests', icon: BookOpen },
  career_exploration: { label: 'Career Exploration',    icon: Briefcase },
  lifestyle_goals:    { label: 'Lifestyle Goals',       icon: DollarSign },
  education_plan:     { label: 'Education Plan',        icon: GraduationCap },
  college_planning:   { label: 'College Planning',      icon: Award },
  scholarships:       { label: 'Scholarships',          icon: TrendingUp },
};

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function CounselorCaseloadPage() {
  const { user } = useAuth();

  // Superintendent / district_admin → show district-wide counselor roster
  if (user?.role === 'superintendent' || user?.role === 'district_admin') {
    return <DistrictCounselorView />;
  }

  return <CounselorPersonalView />;
}

// ─────────────────────────────────────────────────────────────────────────────
// District Counselor Intelligence (superintendent view), 2-level drill-down
// ─────────────────────────────────────────────────────────────────────────────
function DistrictCounselorView() {
  const [schools, setSchools]       = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [q, setQ]                   = useState('');
  const [selectedSchool, setSelectedSchool]       = useState(null);
  const [selectedCounselor, setSelectedCounselor] = useState(null);
  const [counselorStudents, setCounselorStudents] = useState([]);
  const [loadingStudents, setLoadingStudents]     = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/institution/district/dashboard`, auth()),
      axios.get(`${API}/institution/district/counselors`, auth()),
    ])
      .then(([dashRes, cRes]) => {
        setSchools(dashRes.data.school_comparison || []);
        setCounselors(cRes.data.counselors || []);
      })
      .catch(e => setError(e.response?.data?.detail || 'Could not load data'))
      .finally(() => setLoading(false));
  }, []);

  const openCounselor = async (c) => {
    setSelectedCounselor(c);
    setLoadingStudents(true);
    try {
      const res = await axios.get(`${API}/institution/counselor/${c.id}/caseload`, auth());
      setCounselorStudents(res.data.students || []);
    } catch { setCounselorStudents([]); }
    finally { setLoadingStudents(false); }
  };

  if (loading) return <AppLayout><LoadingSpinner /></AppLayout>;
  if (error) return (
    <AppLayout>
      <GlassCard className="p-6">
        <div className="flex items-center gap-3 text-rose-300">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      </GlassCard>
    </AppLayout>
  );

  // ── Level 3: Counselor Student List ──────────────────────────────────────
  if (selectedCounselor) {
    const tierOrder = { needs_attention:0, no_direction:1, incomplete:2, on_track:3 };
    return (
      <AppLayout>
        <div className="space-y-5" data-testid={`counselor-drill-${selectedCounselor.id}`}>
          <div>
            <button onClick={() => { setSelectedCounselor(null); setCounselorStudents([]); }}
              className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors mb-4">
              <ArrowLeft className="h-4 w-4" /> Back to {selectedSchool?.name || 'School'}
            </button>
            <div className="relative overflow-hidden rounded-2xl border border-teal-300/20 bg-[#0a1729] px-8 py-7">
              <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-60 w-60 rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(45,212,191,0.14), transparent 65%)' }} />
              <span className="inline-flex items-center gap-2 rounded-full border border-teal-300/30 bg-teal-400/[0.07]
                px-3 py-1 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-teal-200">
                <Users className="h-3 w-3" /> Counselor Caseload
              </span>
              <h1 className="mt-3 text-2xl font-bold text-white">{selectedCounselor.name}</h1>
              <p className="mt-1 text-sm text-slate-300">{selectedCounselor.total_students} students assigned · {selectedCounselor.needs_attention} need attention</p>
            </div>
          </div>

          {/* Tier summary */}
          <div className="grid gap-3 sm:grid-cols-4">
            {Object.entries(TIER).map(([key, t]) => {
              const cnt = counselorStudents.filter(s => s.tier === key).length;
              return (
                <div key={key} className="rounded-2xl border p-4" style={{ borderColor: t.border, background: t.bg }}>
                  <t.icon className="h-4 w-4 mb-2" style={{ color: t.color }} />
                  <p className="text-2xl font-bold" style={{ color: t.color }}>{cnt}</p>
                  <p className="text-[11px] text-slate-300 mt-0.5">{t.label}</p>
                </div>
              );
            })}
          </div>

          {loadingStudents ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : (
            <div className="rounded-2xl border border-white/[0.07] overflow-hidden" data-testid="counselor-student-list">
              <div className="px-5 py-3 border-b border-white/[0.05] flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-300">Students</p>
                <p className="text-[11px] text-slate-300">{counselorStudents.length} total</p>
              </div>
              <div className="divide-y divide-white/[0.04]">
                {counselorStudents.map(s => {
                  const t = TIER[s.tier] || TIER.incomplete;
                  return (
                    <div key={s.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-white/[0.02] transition-colors">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border"
                        style={{ borderColor: t.border, background: t.bg }}>
                        <t.icon className="h-3.5 w-3.5" style={{ color: t.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white">{s.full_name}</p>
                        <p className="text-[11.5px] text-slate-300 truncate">
                          {s.school ? `${s.school} · ` : ''}{s.career || 'No career set'}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {s.grade && <span className="text-[10.5px] text-slate-300">Gr. {s.grade}</span>}
                        <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold"
                          style={{ color: t.color, background: t.bg, border: `1px solid ${t.border}` }}>
                          {t.label}
                        </span>
                        {s.score > 0 && (
                          <span className="text-[11px] font-bold"
                            style={{ color: s.score >= 65 ? '#34d399' : s.score >= 40 ? '#fbbf24' : '#f87171' }}>
                            {s.score}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {counselorStudents.length === 0 && (
                  <div className="px-5 py-12 text-center text-sm text-slate-300">No students assigned to this counselor.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </AppLayout>
    );
  }

  // ── Level 2: School → Counselors ─────────────────────────────────────────
  // When a school is selected, show its counselors
  if (selectedSchool) {
    const schoolCounselors = counselors.filter(c => c.org_id === selectedSchool.id);
    const totals = schoolCounselors.reduce((a, c) => ({
      total:           a.total           + (c.total_students || 0),
      needs_attention: a.needs_attention + (c.needs_attention || 0),
      incomplete:      a.incomplete      + (c.incomplete || 0),
      on_track:        a.on_track        + (c.on_track || 0),
    }), { total: 0, needs_attention: 0, incomplete: 0, on_track: 0 });

    return (
      <AppLayout>
        <div className="space-y-5" data-testid={`school-drill-${selectedSchool.id}`}>
          {/* Back + school header */}
          <div>
            <button onClick={() => { setSelectedSchool(null); setQ(''); }}
              className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors mb-4">
              <ArrowLeft className="h-4 w-4" /> All Schools
            </button>
            <div className="relative overflow-hidden rounded-2xl border border-teal-300/20 bg-[#0a1729] px-8 py-8">
              <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-60 w-60 rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(45,212,191,0.14), transparent 65%)' }} />
              <span className="inline-flex items-center gap-2 rounded-full border border-teal-300/30 bg-teal-400/[0.07]
                px-3 py-1 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-teal-200">
                <Building className="h-3 w-3" /> School View
              </span>
              <h1 className="mt-3 text-2xl font-bold text-white sm:text-3xl">{selectedSchool.name}</h1>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-300">
                <span>{selectedSchool.total_students} students</span>
                <span>{schoolCounselors.length} counselors</span>
                <span style={{ color: selectedSchool.future_readiness_score >= 65 ? '#34d399' : '#fbbf24' }}>
                  Readiness: {selectedSchool.future_readiness_score}
                </span>
              </div>
            </div>
          </div>

          {/* School priority tiles */}
          <div className="grid gap-3 sm:grid-cols-4">
            {[
              { label: 'Needs Attention', value: totals.needs_attention, color: '#f87171', border: 'rgba(248,113,113,0.28)', bg: 'rgba(248,113,113,0.09)', Icon: AlertTriangle },
              { label: 'In Progress',     value: totals.incomplete,      color: '#fbbf24', border: 'rgba(251,191,36,0.28)',  bg: 'rgba(251,191,36,0.09)',  Icon: Clock },
              { label: 'CDP Complete',    value: totals.on_track,        color: '#34d399', border: 'rgba(52,211,153,0.28)',  bg: 'rgba(52,211,153,0.09)',  Icon: ShieldCheck },
              { label: 'Total Students',  value: totals.total,           color: '#22d3ee', border: 'rgba(34,211,238,0.20)',  bg: 'rgba(34,211,238,0.06)',  Icon: Users },
            ].map(({ label, value, color, border, bg, Icon }) => (
              <div key={label} className="rounded-2xl border p-5" style={{ borderColor: border, background: bg }}>
                <Icon className="h-5 w-5 mb-3" style={{ color }} />
                <p className="text-3xl font-bold tabular-nums" style={{ color }}>{value.toLocaleString()}</p>
                <p className="mt-1 text-[11.5px] font-medium text-slate-300">{label}</p>
              </div>
            ))}
          </div>

          {/* Search within school */}
          <div className="flex items-center gap-3">
            <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
              <Search className="h-3.5 w-3.5 shrink-0 text-teal-300" />
              <input value={q} onChange={e => setQ(e.target.value)}
                placeholder="Search counselors…"
                data-testid="counselor-school-search"
                className="w-full bg-transparent text-xs text-white placeholder:text-slate-300 focus:outline-none" />
            </div>
            <p className="text-[11px] text-slate-300 shrink-0">{schoolCounselors.length} counselors</p>
          </div>

          {/* Counselor list */}
          <div className="space-y-2" data-testid="school-counselor-list">
            {schoolCounselors
              .filter(c => !q || c.name?.toLowerCase().includes(q.toLowerCase()))
              .map(c => <DistrictCounselorCard key={c.id} counselor={c} onSelect={openCounselor} />)
            }
            {schoolCounselors.length === 0 && (
              <GlassCard className="p-8 text-center">
                <Users className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-3 text-sm text-slate-300">No counselors found for this school.</p>
              </GlassCard>
            )}
          </div>
        </div>
      </AppLayout>
    );
  }

  // Default: School list view
  const districtTotals = counselors.reduce((a, c) => ({
    needs_attention: a.needs_attention + (c.needs_attention || 0),
    incomplete:      a.incomplete      + (c.incomplete || 0),
    on_track:        a.on_track        + (c.on_track || 0),
  }), { needs_attention: 0, incomplete: 0, on_track: 0 });

  const filteredSchools = schools.filter(s =>
    !q || s.name?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="district-counselor-view">

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl border border-teal-300/20 bg-[#0a1729] px-8 py-10">
          <div aria-hidden className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(45,212,191,0.16), transparent 65%)' }} />
          <span className="inline-flex items-center gap-2 rounded-full border border-teal-300/30
            bg-teal-400/[0.07] px-3 py-1 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-teal-200">
            <Users className="h-3 w-3" /> District Counselor Intelligence
          </span>
          <h1 className="mt-3 text-3xl font-bold text-white sm:text-4xl">Counselor Command Center</h1>
          <p className="mt-2 text-sm text-slate-300 max-w-xl">
            {schools.length} schools · {counselors.length} counselors. Click a school to drill into its caseload.
          </p>
        </div>

        {/* District-wide priority tiles */}
        <div className="grid gap-3 sm:grid-cols-4" data-testid="district-priority-stats">
          {[
            { label: 'Schools', value: schools.length, color: '#22d3ee', border: 'rgba(34,211,238,0.20)', bg: 'rgba(34,211,238,0.06)', Icon: Building },
            { label: 'Needs Attention (District)', value: districtTotals.needs_attention, color: '#f87171', border: 'rgba(248,113,113,0.28)', bg: 'rgba(248,113,113,0.09)', Icon: AlertTriangle },
            { label: 'In Progress',  value: districtTotals.incomplete, color: '#fbbf24', border: 'rgba(251,191,36,0.28)', bg: 'rgba(251,191,36,0.09)', Icon: Clock },
            { label: 'CDP Complete', value: districtTotals.on_track,   color: '#34d399', border: 'rgba(52,211,153,0.28)', bg: 'rgba(52,211,153,0.09)', Icon: ShieldCheck },
          ].map(({ label, value, color, border, bg, Icon }) => (
            <div key={label} data-testid={`district-tile-${label.replace(/ /g,'-').toLowerCase()}`}
              className="rounded-2xl border p-5" style={{ borderColor: border, background: bg }}>
              <Icon className="h-5 w-5 mb-3" style={{ color }} />
              <p className="text-3xl font-bold tabular-nums" style={{ color }}>{value.toLocaleString()}</p>
              <p className="mt-1 text-[11.5px] font-medium text-slate-300">{label}</p>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="flex items-center gap-3">
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-teal-300" />
            <input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search schools…"
              data-testid="school-search"
              className="w-full bg-transparent text-xs text-white placeholder:text-slate-300 focus:outline-none" />
          </div>
          <p className="text-[11px] text-slate-300 shrink-0">{filteredSchools.length} of {schools.length} schools</p>
        </div>

        {/* School cards grid, click to drill down */}
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="school-cards-grid">
          {filteredSchools.map(school => {
            const schoolCounselorCount = counselors.filter(c => c.org_id === school.id).length;
            const urgentCount = counselors.filter(c => c.org_id === school.id)
              .reduce((a, c) => a + (c.needs_attention || 0), 0);
            const rs = school.future_readiness_score;
            const rsColor = rs >= 65 ? '#34d399' : rs >= 40 ? '#fbbf24' : '#fb923c';
            const eng = school.engagement || {};
            return (
              <button key={school.id} onClick={() => { setSelectedSchool(school); setQ(''); }}
                data-testid={`school-card-${school.id}`}
                className="text-left rounded-2xl border border-white/[0.08] bg-[#0b1b30] p-5
                  hover:border-teal-300/30 hover:bg-teal-400/[0.04] transition-all group">

                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Building className="h-3.5 w-3.5 text-teal-300 shrink-0" />
                      <p className="font-semibold text-white text-sm truncate group-hover:text-teal-200 transition-colors">
                        {school.name}
                      </p>
                    </div>
                    <p className="text-[11px] text-slate-300">
                      {school.total_students} students · {schoolCounselorCount} counselor{schoolCounselorCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-2xl font-bold" style={{ color: rsColor }}>{rs}</p>
                    <p className="text-[9.5px] text-slate-300">readiness</p>
                  </div>
                </div>

                {/* Engagement bars */}
                <div className="space-y-1.5 mb-4">
                  {[
                    { label: 'Assessment', pct: eng.assessment_complete_pct, color: '#a78bfa' },
                    { label: 'Direction',  pct: eng.direction_established_pct, color: '#34d399' },
                    { label: 'Colleges',   pct: eng.college_planning_started_pct, color: '#fbbf24' },
                  ].map(b => (
                    <div key={b.label} className="flex items-center gap-2">
                      <p className="w-16 shrink-0 text-[9.5px] text-slate-300">{b.label}</p>
                      <div className="flex-1 h-1 rounded-full bg-white/[0.05]">
                        <div className="h-1 rounded-full" style={{ width: `${b.pct ?? 0}%`, background: b.color }} />
                      </div>
                      <p className="w-7 shrink-0 text-right text-[9.5px] text-slate-300">{b.pct ?? 0}%</p>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  {urgentCount > 0 ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-400 border border-rose-500/25">
                      {urgentCount} need attention
                    </span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
                      All on track
                    </span>
                  )}
                  <span className="flex items-center gap-1 text-[10.5px] text-teal-400 group-hover:gap-1.5 transition-all">
                    Drill in <ChevronRight className="h-3 w-3" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>

      </div>
    </AppLayout>
  );
}

function DistrictCounselorCard({ counselor: c, onSelect }) {
  const total = c.total_students || 0;
  const hasPriority = c.needs_attention > 0;
  return (
    <button
      onClick={() => onSelect && onSelect(c)}
      data-testid={`counselor-card-${c.id}`}
      className="w-full text-left rounded-xl border p-4 transition-all hover:brightness-110 hover:scale-[1.005] cursor-pointer"
      style={{
        borderColor: hasPriority ? 'rgba(248,113,113,0.25)' : 'rgba(52,211,153,0.15)',
        background: hasPriority ? 'rgba(248,113,113,0.04)' : 'rgba(255,255,255,0.012)',
      }}>
      <div className="flex flex-wrap items-start gap-3">
        {/* Avatar */}
        <div className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center font-bold text-sm"
          style={{ background: 'rgba(45,212,191,0.12)', color: '#2dd4bf' }}>
          {(c.name || '?')[0].toUpperCase()}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-white">{c.name || c.email}</p>
            {hasPriority && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-400 border border-rose-500/25">
                {c.needs_attention} need attention
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Building className="h-3 w-3 text-slate-300 shrink-0" />
            <p className="text-[11px] text-slate-300 truncate">{c.school_name || c.org_id}</p>
          </div>
        </div>

        {/* Priority breakdown */}
        <div className="flex items-center gap-4 text-[11px] flex-wrap">
          <div className="text-center">
            <p className="font-bold text-rose-400">{c.needs_attention}</p>
            <p className="text-slate-300">Urgent</p>
          </div>
          <div className="text-center">
            <p className="font-bold text-amber-400">{c.no_direction + c.incomplete}</p>
            <p className="text-slate-300">Needs work</p>
          </div>
          <div className="text-center">
            <p className="font-bold text-emerald-400">{c.on_track}</p>
            <p className="text-slate-300">On track</p>
          </div>
          <div className="text-center border-l border-white/10 pl-4">
            <p className="font-bold text-white">{total}</p>
            <p className="text-slate-300">Total</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full mt-2">
          <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-white/[0.05]">
            {total > 0 && <>
              <div className="h-1.5 bg-rose-400/80"   style={{ width: `${(c.needs_attention / total) * 100}%` }} />
              <div className="h-1.5 bg-amber-400/70"  style={{ width: `${((c.no_direction + c.incomplete) / total) * 100}%` }} />
              <div className="h-1.5 bg-emerald-400/80" style={{ width: `${(c.on_track / total) * 100}%` }} />
            </>}
          </div>
        </div>
        <div className="w-full flex justify-end mt-1.5">
          <span className="text-[10px] text-slate-300 flex items-center gap-1">
            Click to view students <ChevronRight className="h-2.5 w-2.5" />
          </span>
        </div>
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Counselor Intelligence, the counselor's own command center
// ─────────────────────────────────────────────────────────────────────────────
const NAVY = 'rgba(13,27,46,0.72)';

function Panel({ children, className = '', testId, accent }) {
  return (
    <div data-testid={testId}
      className={`rounded-2xl border backdrop-blur-xl ${className}`}
      style={{ background: NAVY, borderColor: accent || 'rgba(148,180,225,0.16)' }}>
      {children}
    </div>
  );
}

function KpiTile({ icon: Icon, value, label, sub, color = '#7dd3fc', testId, onClick, active }) {
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={testId}
      className={`text-left rounded-2xl border backdrop-blur-xl p-5 transition-all ${onClick ? 'hover:brightness-125' : 'cursor-default'}`}
      style={{
        background: active ? `${color}14` : NAVY,
        borderColor: active ? color : 'rgba(148,180,225,0.16)',
        boxShadow: active ? `0 0 24px ${color}22` : 'none',
      }}>
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">{label}</p>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <p className="mt-3 text-4xl font-bold tabular-nums" style={{ color }}>{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-300">{sub}</p>}
    </button>
  );
}

function MixBar({ label, count, total, color }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-[12.5px] text-slate-200 truncate">{label}</p>
        <p className="text-[12px] text-slate-300 shrink-0 tabular-nums">{count} · {pct}%</p>
      </div>
      <div className="mt-1.5 h-2 rounded-full bg-white/[0.06] overflow-hidden">
        <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

const SEV = {
  high:   { color: '#f87171', bg: 'rgba(248,113,113,0.10)', border: 'rgba(248,113,113,0.30)' },
  medium: { color: '#fbbf24', bg: 'rgba(251,191,36,0.09)',  border: 'rgba(251,191,36,0.28)' },
  low:    { color: '#7dd3fc', bg: 'rgba(125,211,252,0.07)', border: 'rgba(125,211,252,0.22)' },
};

function CounselorPersonalView() {
  const [searchParams] = useSearchParams();
  const [data, setData]               = useState(null);
  const [intel, setIntel]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [q, setQ]                     = useState('');
  const [activeTier, setActiveTier]   = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [focusIds, setFocusIds]       = useState(null);
  const [focusLabel, setFocusLabel]   = useState('');
  const [selected, setSelected]       = useState([]);
  const [groupNote, setGroupNote]     = useState('');
  const [groupSaving, setGroupSaving] = useState(false);
  const [groupDone, setGroupDone]     = useState(0);
  const [groupError, setGroupError]   = useState('');
  const [selectedId, setSelectedId]   = useState(null);
  const [snapshot, setSnapshot]       = useState(null);
  const [snapLoading, setSnapLoading] = useState(false);
  const [tab, setTab]                 = useState(searchParams.get('tab') || 'roster');
  const [quickFilter, setQuickFilter] = useState(null);
  const [showNudge, setShowNudge]     = useState(false);
  const [nudgeIds, setNudgeIds]       = useState([]);
  const [savedKey, setSavedKey]       = useState(0);
  const [sentNudges, setSentNudges]   = useState([]);
  const [toast, setToast]             = useState('');
  const [viewMode, setViewMode]       = useState('cards'); // 'cards' | 'table'
  const [sortKey, setSortKey]         = useState('name');
  const [sortDir, setSortDir]         = useState('asc');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/institution/counselor/dashboard`, auth()),
      axios.get(`${API}/institution/counselor/caseload-intel`, auth()).catch(() => ({ data: null })),
    ])
      .then(([d, i]) => { setData(d.data); setIntel(i.data); })
      .catch(e => setError(e.response?.data?.detail || 'Could not load caseload'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && t !== tab) {
      setTab(t);
      if (t === 'messages') loadSentNudges();
    }
  }, [searchParams]); // eslint-disable-line

  useEffect(() => {
    if (!selectedId) { setSnapshot(null); return; }
    setSnapLoading(true);
    axios.get(`${API}/institution/student-snapshot/${selectedId}`, auth())
      .then(r => setSnapshot(r.data))
      .catch(() => setSnapshot(null))
      .finally(() => setSnapLoading(false));
  }, [selectedId]);

  const saveGroupNote = async () => {
    if (!groupNote.trim() || selected.length === 0) return;
    setGroupSaving(true);
    try {
      const r = await axios.post(`${API}/institution/counselor/group-note`,
        { student_ids: selected, note: groupNote.trim(), note_type: 'group' }, auth());
      setGroupDone(r.data.logged || selected.length);
      setGroupNote(''); setSelected([]);
      setTimeout(() => setGroupDone(0), 4000);
    } catch { setGroupError('Could not log that note, try again'); setTimeout(() => setGroupError(''), 4000); }
    finally { setGroupSaving(false); }
  };

  if (loading) return <AppLayout><LoadingSpinner /></AppLayout>;

  if (error) return (
    <AppLayout>
      <GlassCard className="p-6" data-testid="command-center-error">
        <div className="flex items-center gap-3 text-rose-300">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div>
            <p className="text-sm font-medium">{error}</p>
            <p className="mt-1 text-[11.5px] text-slate-300">
              Counselor, org admin, or facilitator role is required. Your account also needs an organisation scope.
            </p>
          </div>
        </div>
      </GlassCard>
    </AppLayout>
  );

  const counts = data.priority_counts || {};
  const allStudents = data.students || [];
  const eng = intel?.engagement || {};
  const grades = intel?.grade_distribution || [];
  const readiness = intel?.future_readiness_score ?? 0;
  const readyColor = readiness >= 85 ? '#22d3ee' : readiness >= 65 ? '#34d399' : readiness >= 40 ? '#fbbf24' : '#fb923c';

  const filtered = allStudents.filter(s =>
    (activeTier === 'all' || s.priority_tier === activeTier) &&
    (gradeFilter === 'all' || String(s.grade) === gradeFilter) &&
    (!focusIds || focusIds.includes(s.student_id)) &&
    (quickFilter !== 'undecided' || s.priority_tier === 'no_direction' || !s.direction) &&
    (quickFilter !== 'no_assessment' || !s.assessment_done) &&
    (!q || (s.name || '').toLowerCase().includes(q.toLowerCase()))
  );

  const grouped = ['needs_attention', 'no_direction', 'incomplete', 'on_track']
    .map(tier => ({
      tier,
      // Alphabetical within each tier, the user complained the roster felt chaotic.
      students: filtered
        .filter(s => s.priority_tier === tier)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    }))
    .filter(g => g.students.length > 0);

  const exportCsv = () => {
    const head = ['Name', 'Grade', 'Priority', 'Direction', 'Assessment', 'CDP %', 'Colleges', 'Last active'];
    const rows = filtered.map(s => [
      s.name, s.grade || '', TIER[s.priority_tier]?.label || '', s.direction || '',
      s.assessment_done ? 'Complete' : 'Not started', s.cdp_completion ?? 0,
      s.college_count ?? 0, s.last_active || 'Never',
    ]);
    const csv = [head, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `caseload-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSelect = (id) => setSelected(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const openNudge = (ids, label) => {
    setNudgeIds(ids);
    setShowNudge(true);
    if (label) setFocusLabel(label);
  };

  const clearAllFilters = () => {
    setFocusIds(null); setFocusLabel(''); setActiveTier('all');
    setGradeFilter('all'); setQuickFilter(null);
  };

  const printReport = async () => {
    await axios.post(`${API}/counselor/report`, {}, auth()).catch(() => {});
    setSavedKey(k => k + 1);
    window.print();
  };

  const loadSentNudges = () => axios.get(`${API}/counselor/nudges`, auth())
    .then(r => setSentNudges(r.data.nudges || [])).catch(() => setSentNudges([]));

  const pathwayMix = Object.entries(intel?.pathway_distribution || {})
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  const PATH_LABEL = {
    four_year: 'Four-year college', community_college: 'Community college',
    skilled_trades: 'Skilled trades', military: 'Military',
    entrepreneurship: 'Entrepreneurship', workforce_direct: 'Straight to work',
    undecided: 'Undecided',
  };
  const PATH_COLOR = ['#38bdf8', '#34d399', '#fbbf24', '#a78bfa', '#f472b6', '#2dd4bf', '#94a3b8'];

  return (
    <AppLayout>
      <div className="space-y-7 pb-16" data-testid="counselor-command-center">

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden rounded-3xl border px-7 py-8 md:px-10 md:py-10"
          style={{ background: 'linear-gradient(135deg, #0b1f38 0%, #0a1729 60%, #0d2135 100%)',
                   borderColor: 'rgba(125,211,252,0.20)' }}>
          <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full blur-2xl"
            style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.20), transparent 68%)' }} />
          <div className="relative flex flex-wrap items-end justify-between gap-8">
            <div className="min-w-0">
              <span className="inline-flex items-center gap-2 rounded-full border border-sky-300/30 bg-sky-400/[0.08]
                px-3 py-1 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-sky-200">
                <BarChart3 className="h-3 w-3" /> Counselor Intelligence
              </span>
              <h1 className="font-outfit mt-3 text-4xl font-semibold tracking-tight text-white sm:text-5xl">
                Counselor Intelligence
              </h1>
              <p className="mt-2 text-sm text-sky-200/80">{data.counselor?.name} · Apex High School</p>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-300/90">
                {intel?.total_students ?? allStudents.length} students assigned to you. Every number below is live,                 students build it in their own time, so your week starts with decisions, not data entry.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={exportCsv} data-testid="export-caseload-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06]
                    px-4 py-2 text-sm text-slate-200 hover:border-sky-300/50 hover:bg-white/[0.10] transition-colors">
                  <Download className="h-3.5 w-3.5" /> Export caseload
                </button>
                <button onClick={printReport} data-testid="caseload-report-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06]
                    px-4 py-2 text-sm text-slate-200 hover:border-sky-300/50 hover:bg-white/[0.10] transition-colors">
                  <FileText className="h-3.5 w-3.5" /> Caseload report
                </button>
                {selected.length > 0 && (
                  <button onClick={() => openNudge(selected)} data-testid="nudge-selected-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-sky-300/40 bg-sky-400/[0.16]
                      px-4 py-2 text-sm font-semibold text-sky-100 hover:bg-sky-400/[0.26] transition-colors">
                    <Send className="h-3.5 w-3.5" /> Nudge {selected.length} selected
                  </button>
                )}
                {(focusIds || activeTier !== 'all' || gradeFilter !== 'all' || quickFilter) && (
                  <button onClick={clearAllFilters}
                    data-testid="clear-focus-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06]
                      px-4 py-2 text-sm text-slate-300 hover:bg-white/[0.10] transition-colors">
                    <X className="h-3.5 w-3.5" /> Clear filters
                  </button>
                )}
              </div>
            </div>

            {/* Readiness ring */}
            <div className="relative shrink-0">
              <svg width="168" height="168" viewBox="0 0 168 168">
                <circle cx="84" cy="84" r="70" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="13" />
                <circle cx="84" cy="84" r="70" fill="none" stroke={readyColor} strokeWidth="13"
                  strokeLinecap="round" transform="rotate(-90 84 84)"
                  strokeDasharray={2 * Math.PI * 70}
                  strokeDashoffset={2 * Math.PI * 70 * (1 - readiness / 100)} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-outfit text-4xl font-bold leading-none text-white tabular-nums"
                  data-testid="caseload-readiness">{readiness}</span>
                <span className="mt-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-300">Readiness</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── Caseload KPIs ───────────────────────────────────────────────── */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="caseload-kpis">
          <KpiTile icon={Users} value={intel?.total_students ?? allStudents.length}
            label="Students assigned" sub="Show everyone" color="#7dd3fc" testId="kpi-caseload"
            active={!focusIds && activeTier === 'all' && !quickFilter && gradeFilter === 'all'}
            onClick={() => { clearAllFilters(); setTab('roster'); }} />
          <KpiTile icon={Target} value={`${eng.direction_established_pct ?? 0}%`}
            label="Declared a direction" sub={`${eng.undecided_count ?? 0} undecided, tap to see them`} color="#34d399" testId="kpi-direction"
            active={focusLabel === 'No direction chosen'}
            onClick={() => {
              const on = focusLabel === 'No direction chosen';
              setQuickFilter(null); setActiveTier('all'); setGradeFilter('all'); setTab('roster');
              setFocusIds(on ? null : (intel?.undecided_student_ids || []));
              setFocusLabel(on ? '' : 'No direction chosen');
            }} />
          <KpiTile icon={CheckCircle} value={`${eng.assessment_complete_pct ?? 0}%`}
            label="Assessment complete" sub={`Average plan ${eng.avg_cdp_completion ?? 0}% built`} color="#a78bfa" testId="kpi-assessment"
            active={focusLabel === 'Assessment not completed'}
            onClick={() => {
              const on = focusLabel === 'Assessment not completed';
              setQuickFilter(null); setActiveTier('all'); setGradeFilter('all'); setTab('roster');
              setFocusIds(on ? null : (intel?.no_assessment_student_ids || []));
              setFocusLabel(on ? '' : 'Assessment not completed');
            }} />
          <KpiTile icon={AlertTriangle} value={(counts.needs_attention || 0) + (counts.no_direction || 0)}
            label="Need attention" sub="Flagged by priority signals" color="#f87171" testId="kpi-attention"
            active={focusLabel === 'Needs attention'}
            onClick={() => {
              const on = focusLabel === 'Needs attention';
              setQuickFilter(null); setActiveTier('all'); setGradeFilter('all'); setTab('roster');
              setFocusIds(on ? null : allStudents
                .filter(s => s.priority_tier === 'needs_attention' || s.priority_tier === 'no_direction')
                .map(s => s.student_id));
              setFocusLabel(on ? '' : 'Needs attention');
            }} />
        </div>

        <TimeSavedPanel refreshKey={savedKey} />

        {/* ── Tabs ────────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap gap-1.5 rounded-2xl border border-white/10 bg-white/[0.04] p-1.5"
          data-testid="counselor-tabs">
          {[
            { id: 'roster', label: 'My students' },
            { id: 'requirements', label: 'Requirements matrix' },
            { id: 'meetings', label: 'Meetings' },
            { id: 'messages', label: 'Sent messages' },
          ].map(t => (
            <button key={t.id} data-testid={`counselor-tab-${t.id}`}
              onClick={() => { setTab(t.id); if (t.id === 'messages') loadSentNudges(); }}
              className={`rounded-xl px-5 py-2.5 text-sm font-medium transition-colors ${
                tab === t.id
                  ? 'border border-sky-400/30 bg-sky-500/[0.15] text-sky-200'
                  : 'border border-transparent text-slate-300 hover:bg-white/[0.05] hover:text-white'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── FuturePath Change Detected, CDP evolution across the caseload ─ */}
        {tab === 'roster' && (
          <CDPEvolutionAlerts onOpenStudent={(sid) => setSelectedId(sid)} />
        )}

        {/* ── NC § 115C-158.10 Compliance rollup across the caseload ── */}
        {tab === 'roster' && (
          <CDPCaseloadCompliance onOpenStudent={(sid) => setSelectedId(sid)} />
        )}

        {/* ── Where your week goes ────────────────────────────────────────── */}
        {tab === 'roster' && intel?.alerts?.length > 0 && (
          <Panel testId="counselor-alerts" className="overflow-hidden">
            <div className="flex items-center gap-2.5 border-b border-white/[0.07] px-6 py-4">
              <Zap className="h-4 w-4 text-sky-300" />
              <h2 className="font-outfit text-base font-semibold text-white">Where your week goes</h2>
              <span className="ml-auto text-[11px] text-slate-300">Tap to focus the roster</span>
            </div>
            <div className="divide-y divide-white/[0.05]">
              {intel.alerts.map(a => {
                const sev = SEV[a.severity] || SEV.low;
                const canFocus = (a.student_ids || []).length > 0;
                return (
                  <button key={a.id} disabled={!canFocus}
                    data-testid={`alert-${a.id}`}
                    onClick={() => { if (canFocus) { setFocusIds(a.student_ids); setFocusLabel(a.label); setActiveTier('all'); } }}
                    className={`flex w-full items-center gap-4 px-6 py-4 text-left transition-colors ${canFocus ? 'hover:bg-white/[0.04]' : 'cursor-default'}`}>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border text-base font-bold tabular-nums"
                      style={{ color: sev.color, background: sev.bg, borderColor: sev.border }}>
                      {a.count}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-white">{a.label}</p>
                      <p className="text-xs text-slate-300 mt-0.5">{a.action}</p>
                    </div>
                    {canFocus && <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />}
                  </button>
                );
              })}
            </div>
          </Panel>
        )}

        {/* ── Requirements matrix tab ─────────────────────────────────────── */}
        {tab === 'requirements' && (
          <RequirementsMatrix onMessageGroup={(ids, label) => openNudge(ids, label)} />
        )}

        {/* ── Meetings tab ────────────────────────────────────────────────── */}
        {tab === 'meetings' && (
          <MeetingsPanel selectedIds={selected}
            onBooked={() => { setSavedKey(k => k + 1); setToast('Students booked and notified'); }} />
        )}

        {/* ── Sent messages tab ───────────────────────────────────────────── */}
        {tab === 'messages' && (
          <div className="space-y-3" data-testid="sent-messages">
            {sentNudges.length === 0 && (
              <Panel className="p-10 text-center">
                <p className="text-sm text-slate-300">No nudges sent yet. Select students and send one.</p>
              </Panel>
            )}
            {sentNudges.map(n => (
              <Panel key={n.id} className="p-5" testId={`nudge-row-${n.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-sm font-semibold text-white">{n.title}</p>
                  <span className="text-[11px] text-slate-300">{(n.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                  <span className="ml-auto rounded-full border border-sky-300/30 bg-sky-400/[0.10] px-3 py-1 text-[11px] text-sky-200">
                    {n.recipient_count} sent · {n.read_pct}% read
                  </span>
                </div>
                <p className="mt-2 text-[13px] text-slate-300">{n.message}</p>
              </Panel>
            ))}
          </div>
        )}

        {/* ── Caseload intelligence ───────────────────────────────────────── */}
        {tab === 'roster' && (<>
        <div className="grid gap-5 lg:grid-cols-2">
          <Panel testId="career-interest-mix" className="p-6">
            <div className="mb-5 flex items-center gap-2.5">
              <Briefcase className="h-4 w-4 text-sky-300" />
              <h2 className="font-outfit text-base font-semibold text-white">Career interest mix</h2>
            </div>
            <div className="space-y-3.5">
              {(intel?.career_interests || []).map((c, i) => (
                <MixBar key={c.name} label={c.name} count={c.count}
                  total={intel?.total_students || 1} color={PATH_COLOR[i % PATH_COLOR.length]} />
              ))}
              {(intel?.career_interests || []).length === 0 && (
                <p className="text-sm text-slate-300">No declared career interests yet.</p>
              )}
            </div>
          </Panel>

          <Panel testId="pathway-mix" className="p-6">
            <div className="mb-5 flex items-center gap-2.5">
              <GraduationCap className="h-4 w-4 text-sky-300" />
              <h2 className="font-outfit text-base font-semibold text-white">Pathway mix</h2>
            </div>
            <div className="space-y-3.5">
              {pathwayMix.map(([key, val], i) => (
                <MixBar key={key} label={PATH_LABEL[key] || key} count={val}
                  total={intel?.total_students || 1} color={PATH_COLOR[i % PATH_COLOR.length]} />
              ))}
              {pathwayMix.length === 0 && <p className="text-sm text-slate-300">No pathway data yet.</p>}
            </div>
          </Panel>
        </div>

        <Panel testId="grade-spread" className="p-6">
          <div className="mb-5 flex items-center gap-2.5">
            <Users className="h-4 w-4 text-sky-300" />
            <h2 className="font-outfit text-base font-semibold text-white">Grade spread</h2>
            <span className="ml-auto text-[11px] text-slate-300">Tap a grade to filter the roster</span>
          </div>
          <div className="flex flex-wrap gap-3">
            {grades.map(g => (
              <button key={g.grade} data-testid={`grade-chip-${g.grade}`}
                onClick={() => setGradeFilter(gradeFilter === g.grade ? 'all' : g.grade)}
                className={`min-w-[110px] flex-1 rounded-xl border px-4 py-3 text-left transition-colors ${
                  gradeFilter === g.grade
                    ? 'border-sky-300/60 bg-sky-400/[0.12]'
                    : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'}`}>
                <p className="text-2xl font-bold text-white tabular-nums">{g.count}</p>
                <p className="text-[11.5px] text-slate-300">Grade {g.grade}</p>
              </button>
            ))}
          </div>
          {intel?.wbl && (
            <div className="mt-5 flex flex-wrap gap-6 border-t border-white/[0.07] pt-4 text-xs text-slate-300">
              <span><strong className="text-white">{intel.wbl.total_hours}</strong> WBL hours logged</span>
              <span><strong className="text-white">{intel.wbl.students_with_hours}</strong> students with hours</span>
              <span><strong className="text-amber-300">{intel.wbl.unverified_entries}</strong> awaiting verification</span>
            </div>
          )}
        </Panel>

        {/* ── Roster controls ─────────────────────────────────────────────── */}
        <div>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <h2 className="font-outfit text-xl font-semibold text-white">
              {focusLabel || 'My students'}
            </h2>
            <span className="text-[11px] text-slate-300">{filtered.length} of {allStudents.length}</span>

            {/* View toggle: Cards | Table */}
            <div className="inline-flex rounded-full border border-white/10 bg-white/[0.03] p-0.5" data-testid="view-mode-toggle">
              {['cards','table'].map(mode => (
                <button key={mode} onClick={() => setViewMode(mode)}
                  data-testid={`view-mode-${mode}`}
                  className={`rounded-full px-3 py-1.5 text-[11.5px] font-semibold transition-colors capitalize ${
                    viewMode === mode ? 'bg-sky-400/[0.18] text-sky-100' : 'text-slate-300 hover:text-white'
                  }`}>
                  {mode === 'cards' ? 'Cards' : 'Table'}
                </button>
              ))}
            </div>

            <div className="ml-auto flex flex-wrap items-center gap-2">
              {Object.entries(TIER).map(([key, cfg]) => (
                <button key={key} onClick={() => setActiveTier(activeTier === key ? 'all' : key)}
                  data-testid={`tier-stat-${key}`}
                  className="rounded-full border px-3 py-1.5 text-[11.5px] font-semibold transition-colors"
                  style={{
                    color: activeTier === key ? '#0b1f38' : cfg.color,
                    background: activeTier === key ? cfg.color : cfg.bg,
                    borderColor: cfg.border,
                  }}>
                  {cfg.label} {counts[key] || 0}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5">
              <Search className="h-3.5 w-3.5 shrink-0 text-sky-300" />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search students…"
                data-testid="student-search"
                className="w-full bg-transparent text-sm text-white placeholder:text-slate-300 focus:outline-none" />
            </div>
            {selected.length > 0 && (
              <div className="flex flex-1 min-w-[280px] items-center gap-2 rounded-xl border border-sky-300/30 bg-sky-400/[0.07] px-3 py-2">
                <MessageSquare className="h-3.5 w-3.5 shrink-0 text-sky-300" />
                <input value={groupNote} onChange={e => setGroupNote(e.target.value)}
                  placeholder={`Log one note for ${selected.length} students…`}
                  data-testid="group-note-input"
                  className="w-full bg-transparent text-sm text-white placeholder:text-slate-300 focus:outline-none" />
                <button onClick={saveGroupNote} disabled={groupSaving || !groupNote.trim()}
                  data-testid="group-note-save"
                  className="flex shrink-0 items-center gap-1.5 rounded-lg border border-sky-300/40 bg-sky-400/[0.15]
                    px-3 py-1.5 text-xs font-semibold text-sky-200 hover:bg-sky-400/[0.25] disabled:opacity-40 transition-colors">
                  <Send className="h-3 w-3" /> {groupSaving ? 'Logging…' : 'Log for all'}
                </button>
              </div>
            )}
            {groupDone > 0 && (
              <p className="text-xs font-semibold text-emerald-400" data-testid="group-note-confirm">
                Logged for {groupDone} {groupDone === 1 ? 'student' : 'students'}
              </p>
            )}
            {groupError && (
              <p className="text-xs font-semibold text-rose-400" data-testid="group-note-error">{groupError}</p>
            )}
          </div>

          {/* ── Grouped roster (cards) or Table ─────────────────────────── */}
          {viewMode === 'table' ? (
            <StudentTable
              students={filtered}
              selected={selected}
              onCheck={toggleSelect}
              onOpen={setSelectedId}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={(k) => {
                if (sortKey === k) { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }
                else { setSortKey(k); setSortDir('asc'); }
              }}
            />
          ) : (
          <div className="space-y-6" data-testid="student-list">
            {grouped.length === 0 && (
              <Panel className="p-10 text-center">
                <Users className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-3 text-sm text-slate-300">
                  {allStudents.length === 0
                    ? 'No students are linked to your caseload yet.'
                    : 'No students match that filter.'}
                </p>
              </Panel>
            )}
            {grouped.map(({ tier, students }) => {
              const cfg = TIER[tier];
              return (
                <div key={tier} data-testid={`tier-group-${tier}`}>
                  <div className="mb-3 flex items-center gap-2.5">
                    <cfg.icon className="h-4 w-4" style={{ color: cfg.color }} />
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: cfg.color }}>
                      {cfg.label}
                    </p>
                    <span className="text-[11px] text-slate-300">{students.length}</span>
                    <button
                      onClick={() => setSelected(prev => {
                        const ids = students.map(s => s.student_id);
                        const allIn = ids.every(i => prev.includes(i));
                        return allIn ? prev.filter(i => !ids.includes(i)) : [...new Set([...prev, ...ids])];
                      })}
                      data-testid={`select-group-${tier}`}
                      className="ml-auto text-[11px] text-slate-300 hover:text-sky-300 transition-colors">
                      Select group
                    </button>
                  </div>
                  <div className="grid gap-3 xl:grid-cols-2">
                    {students.map(s => (
                      <StudentCard key={s.student_id} student={s}
                        checked={selected.includes(s.student_id)}
                        onCheck={() => toggleSelect(s.student_id)}
                        onOpen={() => setSelectedId(s.student_id)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
        </>)}
      </div>

      {showNudge && (
        <NudgeComposer
          studentIds={nudgeIds}
          onClose={() => setShowNudge(false)}
          onSent={(r) => { setSavedKey(k => k + 1); setSelected([]);
            setToast(`Nudge delivered to ${r.sent} students`); setTimeout(() => setToast(''), 5000); }}
        />
      )}

      {toast && (
        <div data-testid="counselor-toast"
          className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2 rounded-full border border-emerald-400/40
            bg-emerald-500/[0.16] px-6 py-3 text-sm font-semibold text-emerald-200 backdrop-blur-xl">
          {toast}
        </div>
      )}

      {selectedId && (
        <SnapshotOverlay
          snapshot={snapshot}
          loading={snapLoading}
          onClose={() => { setSelectedId(null); setSnapshot(null); }}
          onNoteAdded={(note) => {
            setSnapshot(prev => prev ? {
              ...prev,
              counselor_notes: [note, ...(prev.counselor_notes || [])],
            } : prev);
          }}
        />
      )}
    </AppLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Student Card
// ─────────────────────────────────────────────────────────────────────────────
function StudentCard({ student: s, checked, onCheck, onOpen }) {
  const tier = TIER[s.priority_tier] || TIER.on_track;
  const cdp = s.cdp_completion ?? 0;
  return (
    <div data-testid={`student-card-${s.student_id}`}
      className="rounded-2xl border p-4 backdrop-blur-xl transition-all hover:brightness-110"
      style={{ background: checked ? 'rgba(56,189,248,0.10)' : NAVY,
               borderColor: checked ? 'rgba(125,211,252,0.55)' : tier.border }}>
      <div className="flex items-start gap-3">
        <input type="checkbox" checked={checked} onChange={onCheck}
          data-testid={`select-student-${s.student_id}`}
          className="mt-1 h-4 w-4 shrink-0 accent-sky-400" />
        <button onClick={onOpen} className="flex-1 min-w-0 text-left">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-white">{s.name}</p>
            {s.grade && <span className="text-[11px] text-slate-300">Grade {s.grade}</span>}
            <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
              style={{ color: tier.color, background: tier.bg, border: `1px solid ${tier.border}` }}>
              {tier.label}
            </span>
          </div>
          <p className="mt-1 truncate text-[12.5px] text-slate-300">
            {s.direction
              ? (s.priority_tier === 'no_direction'
                  ? `Interest: ${s.direction}, no path chosen yet`
                  : s.direction)
              : 'No direction set'}
          </p>
          {s.priority_reasons?.[0] && (
            <p className="mt-0.5 truncate text-[11.5px] italic text-slate-300">{s.priority_reasons[0]}</p>
          )}

          {/* Plan progress */}
          <div className="mt-3 flex items-center gap-2">
            <div className="h-1.5 flex-1 rounded-full bg-white/[0.07] overflow-hidden">
              <div className="h-1.5 rounded-full"
                style={{ width: `${cdp}%`, background: cdp >= 75 ? '#34d399' : cdp >= 40 ? '#fbbf24' : '#f87171' }} />
            </div>
            <span className="w-16 shrink-0 text-right text-[11px] text-slate-300 tabular-nums">Plan {cdp}%</span>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-slate-300">
            <QuickStat label={s.assessment_done ? 'Assessment' : 'No assessment'} ok={s.assessment_done} />
            <QuickStat label={`${s.college_count || 0} colleges`} ok={(s.college_count || 0) > 0} />
            {s.upcoming_deadlines > 0 && (
              <span className="flex items-center gap-1 text-amber-300">
                <Calendar className="h-3 w-3" /> {s.upcoming_deadlines} due
              </span>
            )}
            <span className="text-slate-300">{s.last_active ? `Active ${timeAgo(s.last_active)}` : 'Not signed in yet'}</span>
            <span className="ml-auto flex items-center gap-1 rounded-lg border border-sky-300/25 bg-sky-400/[0.08]
              px-2.5 py-1 text-[11px] font-medium text-sky-200">
              Open brief <ChevronRight className="h-3 w-3" />
            </span>
          </div>
        </button>
      </div>
    </div>
  );
}

function QuickStat({ label, ok }) {
  return (
    <span className="flex items-center gap-1">
      {ok
        ? <CheckCircle className="h-3 w-3 text-emerald-400" />
        : <XCircle className="h-3 w-3 text-rose-400" />}
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sortable Table view, user requested column-based sorting
// ─────────────────────────────────────────────────────────────────────────────
const TIER_ORDER = { needs_attention: 0, no_direction: 1, incomplete: 2, on_track: 3 };

function SortIcon({ colKey, sortKey, sortDir }) {
  if (sortKey !== colKey) return <ArrowUpDown className="h-3 w-3 text-slate-300" />;
  return sortDir === 'asc'
    ? <ArrowUp className="h-3 w-3 text-sky-300" />
    : <ArrowDown className="h-3 w-3 text-sky-300" />;
}

function StudentTable({ students, selected, onCheck, onOpen, sortKey, sortDir, onSort }) {
  const columns = [
    { key: 'name',        label: 'Student',     width: 'min-w-[180px]' },
    { key: 'grade',       label: 'Grade',       width: 'w-16' },
    { key: 'priority',    label: 'Priority',    width: 'w-40' },
    { key: 'direction',   label: 'Direction',   width: 'min-w-[160px]' },
    { key: 'cdp',         label: 'Plan %',      width: 'w-20' },
    { key: 'assessment',  label: 'Assessment',  width: 'w-28' },
    { key: 'colleges',    label: 'Colleges',    width: 'w-20' },
    { key: 'last_active', label: 'Last active', width: 'w-28' },
  ];

  const sorted = [...students].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const get = (s) => {
      switch (sortKey) {
        case 'name': return (s.name || '').toLowerCase();
        case 'grade': return s.grade || 0;
        case 'priority': return TIER_ORDER[s.priority_tier] ?? 99;
        case 'direction': return (s.direction || 'zzz').toLowerCase();
        case 'cdp': return s.cdp_completion ?? 0;
        case 'assessment': return s.assessment_done ? 1 : 0;
        case 'colleges': return s.college_count ?? 0;
        case 'last_active': return s.last_active ? new Date(s.last_active).getTime() : 0;
        default: return 0;
      }
    };
    const va = get(a), vb = get(b);
    if (va < vb) return -1 * dir;
    if (va > vb) return  1 * dir;
    return 0;
  });

  return (
    <div className="rounded-2xl border border-white/10 overflow-hidden"
      style={{ background: 'rgba(13,27,46,0.72)' }}
      data-testid="student-table">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-white/[0.07] bg-white/[0.02]">
              <th className="w-10 px-3 py-3"></th>
              {columns.map(c => (
                <th key={c.key}
                  data-testid={`sort-col-${c.key}`}
                  onClick={() => onSort(c.key)}
                  className={`${c.width} px-3 py-3 text-[10.5px] font-semibold uppercase tracking-[0.14em]
                    text-slate-300 hover:text-sky-200 cursor-pointer select-none`}>
                  <span className="inline-flex items-center gap-1.5">
                    {c.label} <SortIcon colKey={c.key} sortKey={sortKey} sortDir={sortDir} />
                  </span>
                </th>
              ))}
              <th className="w-10 px-3 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.04]">
            {sorted.length === 0 && (
              <tr><td colSpan={10} className="px-6 py-10 text-center text-sm text-slate-300">
                No students match that filter.
              </td></tr>
            )}
            {sorted.map(s => {
              const tier = TIER[s.priority_tier] || TIER.on_track;
              const cdp = s.cdp_completion ?? 0;
              const cdpColor = cdp >= 75 ? '#34d399' : cdp >= 40 ? '#fbbf24' : '#f87171';
              const isSel = selected.includes(s.student_id);
              return (
                <tr key={s.student_id}
                  data-testid={`student-row-${s.student_id}`}
                  className={`transition-colors ${isSel ? 'bg-sky-400/[0.08]' : 'hover:bg-white/[0.03]'}`}>
                  <td className="px-3 py-3">
                    <input type="checkbox" checked={isSel}
                      onChange={() => onCheck(s.student_id)}
                      data-testid={`row-select-${s.student_id}`}
                      className="h-4 w-4 accent-sky-400 cursor-pointer" />
                  </td>
                  <td className="px-3 py-3">
                    <button onClick={() => onOpen(s.student_id)}
                      data-testid={`row-open-${s.student_id}`}
                      className="text-left text-sm font-semibold text-white hover:text-sky-200 transition-colors">
                      {s.name}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-[12px] text-slate-300 tabular-nums">
                    {s.grade || '-'}
                  </td>
                  <td className="px-3 py-3">
                    <span className="inline-flex rounded-full px-2 py-0.5 text-[10.5px] font-semibold"
                      style={{ color: tier.color, background: tier.bg, border: `1px solid ${tier.border}` }}>
                      {tier.label}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-[12px] text-slate-300 truncate max-w-[220px]">
                    {s.direction || <span className="text-slate-300">-</span>}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-14 rounded-full bg-white/[0.06] overflow-hidden">
                        <div className="h-1.5 rounded-full" style={{ width: `${cdp}%`, background: cdpColor }} />
                      </div>
                      <span className="text-[11px] text-slate-300 tabular-nums w-8">{cdp}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    {s.assessment_done
                      ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300"><CheckCircle className="h-3 w-3" /> Done</span>
                      : <span className="inline-flex items-center gap-1 text-[11px] text-slate-300"><XCircle className="h-3 w-3" /> Missing</span>}
                  </td>
                  <td className="px-3 py-3 text-[12px] text-slate-300 tabular-nums">
                    {s.college_count ?? 0}
                  </td>
                  <td className="px-3 py-3 text-[11px] text-slate-300">
                    {s.last_active ? timeAgo(s.last_active) : <span className="text-slate-300">Never</span>}
                  </td>
                  <td className="px-3 py-3">
                    <button onClick={() => onOpen(s.student_id)}
                      className="text-slate-300 hover:text-sky-300 transition-colors">
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Snapshot Overlay, "Before your meeting"
// ─────────────────────────────────────────────────────────────────────────────
function SnapshotOverlay({ snapshot: snap, loading, onClose, onNoteAdded }) {
  const [note, setNote]                     = useState('');
  const [noteType, setNoteType]             = useState('meeting');
  const [noteParentVisible, setNoteParentVisible] = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [saved, setSaved]                   = useState(false);
  const [verifyingWbl, setVerifyingWbl]     = useState(null);
  const [wblEntries, setWblEntries]         = useState(null);

  // Load WBL entries for this student when snap loads
  useEffect(() => {
    if (!snap?.student?.id) return;
    axios.get(`${API}/nc/wbl`, { ...auth(), params: { student_id: snap.student.id } })
      .then(r => setWblEntries(r.data?.entries || r.data?.wbl_experiences || []))
      .catch(() => setWblEntries([]));
  }, [snap?.student?.id]);

  const verifyWbl = async (wblId) => {
    setVerifyingWbl(wblId);
    try {
      await axios.put(`${API}/institution/wbl/${wblId}/verify`, {}, auth());
      setWblEntries(prev => (prev || []).map(w => w.id === wblId ? { ...w, status: 'verified', verified: true } : w));
    } catch { /* silent */ }
    finally { setVerifyingWbl(null); }
  };

  const submitNote = useCallback(async () => {
    if (!note.trim() || !snap?.student?.id) return;
    setSaving(true);
    try {
      await axios.post(`${API}/institution/counselor/notes`,
        {
          student_id: snap.student.id,
          note: note.trim(),
          note_type: noteType,
          parent_visible: noteParentVisible,
        },
        auth()
      );
      onNoteAdded({
        note: note.trim(),
        note_type: noteType,
        parent_visible: noteParentVisible,
        created_at: new Date().toISOString(),
        counselor_name: 'You',
      });
      setNote('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { /* silent */ }
    finally { setSaving(false); }
  }, [note, noteType, noteParentVisible, snap, onNoteAdded]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-[#020b12] backdrop-blur-xl"
      style={{ background: 'linear-gradient(180deg, #020814 0%, #050f1e 100%)' }}
      data-testid="student-snapshot-overlay">
      {/* Close bar */}
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/[0.08]
        bg-[#050f1e] px-6 py-3 shadow-lg"
        style={{ background: 'rgba(5,15,30,0.98)' }}>
        <button onClick={onClose}
          className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to caseload
        </button>
        {snap && (
          <p className="text-xs text-slate-300">
            {snap.student.name} · Grade {snap.student.grade}
            {snap.student.school && <> · {snap.student.school}</>}
          </p>
        )}
        <button onClick={onClose}
          className="rounded-lg p-1.5 text-slate-300 hover:bg-white/[0.07] hover:text-white transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      {loading && (
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      )}

      {!loading && !snap && (
        <div className="flex h-64 items-center justify-center text-sm text-slate-300">
          Could not load snapshot.
        </div>
      )}

      {!loading && snap && (
        <div className="mx-auto max-w-6xl px-6 py-8">
          {/* Brief header */}
          <div className="mb-8">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-400">
              Before your meeting
            </p>
            <h2 className="mt-1 text-2xl font-semibold text-white sm:text-3xl">
              {snap.student.name}
            </h2>
            <p className="mt-1 text-sm text-slate-300">
              {[
                snap.student.grade ? `Grade ${snap.student.grade}` : null,
                snap.student.age ? `${snap.student.age} yrs` : null,
                snap.student.state || null,
              ].filter(Boolean).join(' · ')}
            </p>
            {/* Completion bar */}
            <div className="mt-4 flex items-center gap-3">
              <div className="h-1.5 flex-1 max-w-xs rounded-full bg-white/[0.06]">
                <div className="h-1.5 rounded-full bg-gradient-to-r from-teal-400 to-cyan-400 transition-all"
                  style={{ width: `${snap.scores?.completion_pct ?? 0}%` }} />
              </div>
              <span className="text-[11.5px] text-slate-300">
                {snap.scores?.completed_modules}/{snap.scores?.total_modules} modules complete
              </span>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {/* ── Left column (2/3) ──────────────────────────────────────── */}
            <div className="space-y-5 lg:col-span-2">

              {/* Direction */}
              {snap.direction?.name && (
                <SnapSection title="Direction" icon={Target}>
                  <p className="text-base font-semibold text-white">{snap.direction.name}</p>
                  {snap.direction.type && (
                    <p className="mt-0.5 text-[11.5px] capitalize text-slate-300">
                      {snap.direction.type.replace(/_/g, ' ')} path
                    </p>
                  )}
                  {snap.assessment?.holland_code && (
                    <p className="mt-2 text-[11.5px] text-teal-300">
                      Holland code: <strong>{snap.assessment.holland_code}</strong>
                      {snap.assessment.type_label && <> · {snap.assessment.type_label}</>}
                    </p>
                  )}
                  {snap.direction.why?.length > 0 && (
                    <p className="mt-1 text-[11.5px] text-slate-300">
                      Backed by: {snap.direction.why.slice(0, 3).join(', ')}
                    </p>
                  )}
                </SnapSection>
              )}

              {/* Progress modules */}
              <SnapSection title="FuturePath Progress" icon={CheckCircle}>
                <div className="grid gap-2 sm:grid-cols-2">
                  {Object.entries(snap.progress || {}).map(([key, status]) => {
                    const meta = MODULE_META[key];
                    if (!meta) return null;
                    const done = status === 'Complete' || status?.includes('school') || status?.includes('saved');
                    const unknown = status === 'Unknown';
                    return (
                      <div key={key}
                        className="flex items-center gap-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                        {unknown
                          ? <Clock className="h-3.5 w-3.5 text-slate-300" />
                          : done
                          ? <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />
                          : <XCircle className="h-3.5 w-3.5 text-rose-400/70" />}
                        <div className="min-w-0">
                          <p className="text-[11.5px] font-medium text-slate-200 leading-tight">{meta.label}</p>
                          <p className={`text-[10.5px] leading-tight ${done ? 'text-emerald-400' : unknown ? 'text-slate-300' : 'text-slate-300'}`}>
                            {status}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </SnapSection>

              {/* College planning */}
              {snap.college_planning?.total > 0 && (
                <SnapSection title={`College Planning, ${snap.college_planning.total} saved`} icon={GraduationCap}>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {snap.college_planning.schools.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 rounded-lg border border-white/[0.06]
                        bg-white/[0.02] px-3 py-2">
                        <div className="h-2 w-2 rounded-full bg-teal-400/70 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-[11.5px] font-medium text-slate-200 truncate">{c.name}</p>
                          {c.city && <p className="text-[10.5px] text-slate-300">{c.city}, {c.state}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </SnapSection>
              )}

              {/* Skills */}
              {snap.skills?.count > 0 && (
                <SnapSection title={`Skills, ${snap.skills.count} on record`} icon={Award}>
                  <div className="flex flex-wrap gap-2">
                    {(snap.skills.top || []).slice(0, 6).map((sk, i) => (
                      <span key={i} className="rounded-full border border-white/[0.08] bg-white/[0.03]
                        px-2.5 py-1 text-[11px] text-slate-300">
                        {typeof sk === 'string' ? sk : (sk?.name || sk?.skill || '')}
                      </span>
                    ))}
                  </div>
                </SnapSection>
              )}
            </div>

            {/* ── Right column (1/3) ─────────────────────────────────────── */}
            <div className="space-y-5">

              {/* Counselor Recommendation, hero element */}
              <div className="rounded-xl border p-5"
                style={{ borderColor: 'rgba(251,191,36,0.35)', background: 'rgba(251,191,36,0.07)' }}
                data-testid="counselor-recommendation">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className="h-4 w-4 text-amber-300" />
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-300">
                    Counselor Recommendation
                  </p>
                </div>
                <p className="text-sm leading-relaxed text-slate-200">
                  {snap.counselor_recommendation}
                </p>
              </div>

              {/* Financial */}
              {snap.financial?.income_goal && (
                <SnapSection title="Financial Picture" icon={DollarSign}>
                  <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-3">
                    <p className="text-[11px] text-slate-300">Income goal</p>
                    <p className="text-xl font-bold text-white">{money(snap.financial.income_goal)}</p>
                  </div>
                </SnapSection>
              )}

              {/* Upcoming goals */}
              {snap.upcoming_goals?.length > 0 && (
                <SnapSection title="Upcoming Deadlines" icon={Calendar}>
                  <div className="space-y-2">
                    {snap.upcoming_goals.map((g, i) => (
                      <div key={i} className="flex items-start gap-2 rounded-lg border border-white/[0.06]
                        bg-white/[0.02] px-3 py-2">
                        <div className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${
                          (g.days_until ?? 99) <= 7 ? 'bg-rose-400' :
                          (g.days_until ?? 99) <= 14 ? 'bg-amber-400' : 'bg-slate-500'}`} />
                        <div className="min-w-0">
                          <p className="text-[11.5px] text-slate-200 leading-tight">{g.title}</p>
                          <p className="text-[10.5px] text-slate-300">
                            {g.days_until != null
                              ? g.days_until === 0 ? 'Today'
                              : g.days_until === 1 ? 'Tomorrow'
                              : `In ${g.days_until} days`
                              : g.due_date}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </SnapSection>
              )}

              {/* WBL Verification, counselor verifies student hours */}
              <SnapSection title="Work-Based Learning" icon={Briefcase}>
                {wblEntries === null ? (
                  <p className="text-[11px] text-slate-300">Loading…</p>
                ) : wblEntries.length === 0 ? (
                  <p className="text-[11px] text-slate-300">No WBL entries recorded.</p>
                ) : (
                  <div className="space-y-2">
                    {wblEntries.map((w, i) => (
                      <div key={w.id || i} className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 ${
                        w.status === 'verified'
                          ? 'bg-emerald-500/8 border-emerald-500/20'
                          : 'bg-slate-800/30 border-slate-700/40'
                      }`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11.5px] font-medium text-white truncate">{w.title || w.type}</p>
                          <p className="text-[10.5px] text-slate-300">{w.employer} · {w.hours}h</p>
                        </div>
                        {w.status === 'verified' ? (
                          <span className="text-[10px] text-emerald-400 font-semibold">✓ Verified</span>
                        ) : (
                          <button
                            onClick={() => verifyWbl(w.id)}
                            disabled={verifyingWbl === w.id}
                            data-testid={`verify-wbl-${w.id}`}
                            className="shrink-0 px-2.5 py-1 rounded-lg text-[10.5px] font-semibold
                              bg-amber-500/15 text-amber-300 border border-amber-500/25
                              hover:bg-amber-500/25 disabled:opacity-50 transition-colors"
                          >
                            {verifyingWbl === w.id ? '…' : 'Verify'}
                          </button>
                        )}
                      </div>
                    ))}
                    <p className="text-[10px] text-slate-300 mt-1">
                      Verifying hours confirms NC WBL requirement compliance.
                    </p>
                  </div>
                )}
              </SnapSection>

              {/* Counselor Notes, with structured types + parent visibility */}
              <SnapSection title="Meeting Notes" icon={MessageSquare}>
                {/* Add note */}
                <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3 space-y-2">
                  {/* Note type selector */}
                  <div className="flex gap-1.5 flex-wrap">
                    {[
                      { id: 'meeting',        label: 'Meeting' },
                      { id: 'annual_review',  label: 'Annual Review' },
                      { id: 'wbl_checkin',    label: 'WBL Check-in' },
                      { id: 'alert',          label: 'Alert' },
                      { id: 'general',        label: 'General' },
                    ].map(t => (
                      <button
                        key={t.id}
                        onClick={() => setNoteType ? setNoteType(t.id) : null}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${
                          (noteType || 'meeting') === t.id
                            ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30'
                            : 'bg-slate-800/50 text-slate-300 border-slate-700/40 hover:text-slate-300'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder="Add a note about this meeting…"
                    data-testid="note-input"
                    rows={3}
                    className="w-full bg-transparent text-[11.5px] text-slate-200
                      placeholder:text-slate-300 focus:outline-none resize-none" />
                  <div className="flex items-center justify-between mt-1">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={noteParentVisible || false}
                        onChange={e => setNoteParentVisible ? setNoteParentVisible(e.target.checked) : null}
                        className="rounded border-slate-600 bg-slate-800 accent-cyan-500"
                      />
                      <span className="text-[10.5px] text-slate-300">Visible to parent</span>
                    </label>
                    <div className="flex items-center gap-2">
                      {saved && <p className="text-[10.5px] text-emerald-400">Saved</p>}
                      <button onClick={submitNote} disabled={saving || !note.trim()}
                        data-testid="save-note-btn"
                        className="flex items-center gap-1.5 rounded-lg border border-teal-300/25
                          bg-teal-400/[0.08] px-3 py-1.5 text-[11px] font-medium text-teal-300
                          hover:bg-teal-400/[0.15] disabled:opacity-40 transition-colors">
                        <Send className="h-3 w-3" />
                        {saving ? 'Saving…' : 'Save note'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Past notes */}
                {snap.counselor_notes?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {snap.counselor_notes.map((n, i) => (
                      <div key={i} className="rounded-lg border border-white/[0.05] bg-white/[0.02] px-3 py-2.5">
                        <div className="flex items-center gap-2 mb-1">
                          {n.note_type && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-700/50 text-slate-300 uppercase tracking-wide">
                              {n.note_type.replace('_', ' ')}
                            </span>
                          )}
                          {n.parent_visible && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/20">
                              Parent visible
                            </span>
                          )}
                        </div>
                        <p className="text-[11.5px] leading-relaxed text-slate-300">{n.note}</p>
                        <p className="mt-1 text-[10px] text-slate-300">
                          {n.counselor_name || n.counselor || 'Counselor'} · {timeAgo(n.created_at || n.date)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </SnapSection>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reusable snapshot section
// ─────────────────────────────────────────────────────────────────────────────
function SnapSection({ title, icon: Icon, children }) {
  return (
    <div className="rounded-xl border border-white/[0.10] p-4"
      style={{ background: 'rgba(11,27,50,0.85)', boxShadow: '0 10px 30px -20px rgba(0,0,0,0.6)' }}>
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-cyan-300" />
        <p className="text-[11px] font-semibold uppercase tracking-wider text-cyan-200/90">{title}</p>
      </div>
      {children}
    </div>
  );
}
